import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { alias, empty, lt, notEmpty, readOnly } from '@ember/object/computed';
import Controller, { inject as controller } from '@ember/controller';
import { action, computed } from '@ember/object';
import { isBlank } from '@ember/utils';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import pluralize from 'cio/utils/pluralize';
import { formatNumber } from 'cio/helpers/format-number';
import { queryDecode, queryEncode, safeQueryDecode } from 'cio/helpers/query';
import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import FeaturesService from 'ember-feature-flags/services/features';
import CustomerService from 'cio/services/customer';
import SettingsService from 'cio/services/settings';
import IndexController from 'cio/pods/people/list/index/controller';
import CustomerModel from 'cio/models/customer';
import AuthService from 'cio/services/auth';
import { Filters, isEmptyFiltersQuery } from 'cio/utils/filters';
import serializeFilters from 'cio/utils/conditions/serialize/filter';
import deserializeFilters from 'cio/utils/conditions/deserialize/filter';
import AnalyticsService from 'cio/services/analytics';
import { SerializedLegacyFilter, SerializedLegacyMessageFilter } from 'cio/types/conditions/legacy-filter';
import { IdentifierKey } from 'cio/utils/identifiers';
import EnvironmentService from 'cio/services/environment';
import isDestroyed from 'cio/utils/is-destroyed';
import RouterService from '@ember/routing/router-service';
import { ConditionType } from 'cio/utils/condition-types';

// Fixed columns: checkbox, unsubscribed, email, created
const FIXED_COLUMN_COUNT = 4;

const HIDDEN_ATTRIBUTES = ['created_at', 'email'];

export default class PeopleListController extends Controller {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service('customer') customerService!: CustomerService;
  @service settings!: SettingsService;
  @service router!: RouterService;

  @tracked showDeleteModal = false;
  @tracked showRelationshipsModal = false;

  supportedConditionTypes = [
    'segments_negatable',
    'attribute',
    'device_attribute',
    'newsletter_recipients',
    'message',
    'event',
  ];

  activeSearchBy = 'email';
  showColumnsModal = false;
  totalRecs!: number;
  people!: CustomerModel[];

  @controller('people.list.index') listIndex!: IndexController;
  @alias('listIndex.email') email!: IndexController['email'];
  @alias('listIndex.filters') filters!: IndexController['filters'];
  @alias('listIndex.page') page!: IndexController['page'];

  @alias('listIndex.allSelected') allSelected!: IndexController['allSelected'];

  @readOnly('listIndex.selectedPeople')
  selectedPeople!: IndexController['selectedPeople'];

  @computed('selectedPeople.@each.identifiers')
  get selectedIds() {
    return this.selectedPeople.map((p) => p.identifiers[IdentifierKey.CioId]);
  }

  @empty('selectedPeople') noPeopleSelected!: IndexController['selectedPeople'];

  get canEdit() {
    return this.auth.hasPermission('journeys/people/edit');
  }

  get actionsDisabled() {
    return Boolean(this.noPeopleSelected) || !this.canEdit;
  }

  get mergePeopleTooltip() {
    if (!this.canEdit) {
      return 'Contact an Admin to access this feature';
    }

    return 'You can only merge 2 people at a time';
  }

  get conditionTypes() {
    let conditionTypes = this.supportedConditionTypes;
    if (!this.environment.enabledAction('push')) {
      conditionTypes = conditionTypes.filter((t) => t !== ConditionType.DeviceAttribute);
    }

    // TODO: waiting for BE
    // if (this.features.isEnabled('campaign_trigger_ui_new')) {
    //   conditionTypes.push(ConditionType.RelationshipAttribute);
    // }

    return conditionTypes;
  }

  @computed('totalRecs', 'selectedPeople.[]', 'allSelected')
  get canUnsubscribe() {
    return !(this.allSelected && this.totalRecs > this.selectedPeople.length) && this.selectedPeople.length > 0;
  }

  @computed('totalRecs', 'selectedPeople.[]', 'allSelected', 'noFiltersApplied')
  get canDelete() {
    if (this.allSelected && this.totalRecs > this.selectedPeople.length) {
      return !this.noFiltersApplied;
    }

    return this.selectedPeople.length > 0;
  }

  @computed('totalRecs', 'selectedPeople.[]', 'allSelected')
  get isBulkDeletion() {
    return this.allSelected && this.totalRecs > this.selectedPeople.length;
  }

  @computed('selectedPeople.[]')
  get peopleMergeLinkParams() {
    let params: Record<string, any> = {
      source: 'people_list',
      email: this.email,
      filters: this.filters,
    };

    if (this.selectedPeople.length === 1) {
      params.primaryCustomerId = this.selectedPeople[0].identifiers[IdentifierKey.CioId];
    } else if (this.selectedPeople.length === 2) {
      let peopleByOldestFirst = [...this.selectedPeople]
        .sort((a, b) => Number(a.createdAt) - Number(b.createdAt))
        .map((p) => p.identifiers[IdentifierKey.CioId]);

      params.primaryCustomerId = peopleByOldestFirst[0];
      params.secondaryCustomerId = peopleByOldestFirst[1];
    }

    return params;
  }

  @computed('totalRecs', 'selectedPeople.[]', 'allSelected')
  get mergeActionDisabled() {
    let { totalRecs, selectedPeople, allSelected } = this;
    return !this.canEdit || selectedPeople.length > 2 || (allSelected && totalRecs > 2) || totalRecs < 2;
  }

  @computed('people', 'selectedPeople.[]', 'allSelected', 'totalRecs', 'itemsPerPage')
  get allPeopleOnPageSelected() {
    if (this.selectedPeople == null || this.people == null || this.people.length === 0) {
      return false;
    }

    if (this.allSelected && this.totalRecs > this.selectedPeople.length) {
      return false;
    }

    return this.selectedPeople.length === this.people.length;
  }

  @computed('selectedPeople.[]', 'allSelected', 'totalRecs')
  get allPeopleOnPageIndeterminate() {
    if (this.allSelected && this.totalRecs > this.selectedPeople.length) {
      return false;
    }

    return (this.selectedPeople?.length ?? 0) > 0 && !this.allPeopleOnPageSelected;
  }

  // e-mail searches with over 2000 matches will return -1 (unknown) total:
  @lt('totalRecs', 0) unknownTotalPeopleCount!: boolean;

  @computed('people.length', 'unknownTotalPeopleCount')
  get selectAllDisabled() {
    let { canEdit, people, unknownTotalPeopleCount } = this;

    return !canEdit || unknownTotalPeopleCount || !people?.length;
  }

  searchInput = '';

  itemsPerPage = 50;

  message = null;
  @notEmpty('message') hasMessage!: boolean;

  hiddenAttributes = HIDDEN_ATTRIBUTES;
  extraAttributes = ['unsubscribed'];

  @computed('settings.environmentSettings.customerColumns.length')
  get totalColumns() {
    return FIXED_COLUMN_COUNT + this.settings.environmentSettings.customerColumns.length;
  }

  @computed('email', 'activeSearchBy')
  get emailCondition() {
    if (this.activeSearchBy !== 'email' || isBlank(this.email)) {
      return null;
    }
    return this.email;
  }

  @computed('filters')
  get filterConditions() {
    return safeQueryDecode(this.filters ?? '');
  }

  @computed('filters')
  get emptyFilters() {
    return isEmptyFiltersQuery(this.filters);
  }

  @computed('totalRecs')
  get peopleToSelectCount() {
    let noun = pluralize(this.totalRecs, 'person');
    return `${formatNumber([this.totalRecs])} ${noun}`;
  }
  @computed('allSelected', 'totalRecs', 'selectedPeople.length')
  get selectedPeopleCountFormatted() {
    let count = this.allSelected ? this.totalRecs : this.selectedPeople.length;
    let noun = pluralize(count, 'person');
    return `${formatNumber([count])} selected ${noun}`;
  }

  @computed('filters', 'email')
  get noFiltersApplied() {
    return (
      (!this.filters || this.emptyFilters) && // guarding against this.filters equal to [] or { and: [] } or { or: [] } (truthy)
      (!this.email?.trim() || this.email.trim() === '@') // whitespace-only or '@' e-mail searches are not considered
    );
  }

  _buildBulkActionRequestPayload() {
    let { filters, email, isBulkDeletion, selectedIds, noFiltersApplied } = this;

    if (!isBulkDeletion || noFiltersApplied) {
      // selected individual customers, or all customers when there is only one page of results
      return { type: 'internal_ids', internal_ids: selectedIds };
    } else if (filters) {
      // selected all customers by clicking the 'Select all XYZ people' button
      // (which is only visible when there are conditions applied)
      let jsonFilters = safeQueryDecode(filters);
      let filtersToSend = jsonFilters;

      if (Array.isArray(jsonFilters)) {
        filtersToSend = serializeFilters(jsonFilters as SerializedLegacyFilter[], 'and', true);
      }

      return {
        type: 'filters',
        filters: filtersToSend,
      };
    } else if (email) {
      return { type: 'email', email };
    }

    return;
  }

  @action
  activateSearchBy(activeSearchBy: 'email' | 'filter') {
    if (this.activeSearchBy !== activeSearchBy) {
      this.setProperties({
        activeSearchBy,
        searchInput: '',
        email: '',
        filters: null,
      });
    }
  }

  @action
  search(term: string) {
    this.setProperties({
      page: 1,
      email: term,
    });
    if (!isDestroyed(this)) {
      this.send('reloadModel'); // reload list of customers to ensure latest results are shown (considering changes made in the meantime by backfills or on other browser tabs)
    }
  }

  @action
  clearSearch() {
    this.set('searchInput', '');
    this.set('email', '');
  }

  @action
  clearFilters() {
    this.set('filters', null);
  }

  @action
  updateFilters(conditions: SerializedLegacyFilter[] | Filters) {
    let filterString = queryEncode(conditions);

    // Reset the query param to nothing
    if (isEmptyFiltersQuery(filterString)) {
      this.set('filters', null);
    } else {
      this.set('filters', filterString);
    }

    this.set('page', 1);
  }

  @action
  confirmDelete({ suppress }: { suppress: boolean }) {
    let data = { ...this._buildBulkActionRequestPayload(), suppress };
    let promise = this.api.post('customers/delete', { data });

    this.apiErrors.attach(promise);

    promise.then(() => {
      this.send('reloadModel');
    }, ignorePromiseFailure);

    return promise;
  }

  @action
  confirmUnsubscribe() {
    let { selectedPeopleCountFormatted, selectedIds } = this;

    let data = { internal_ids: selectedIds };
    let promise = this.api.post('customers/unsubscribe', { data });

    this.apiErrors.attach(promise);

    promise.then(() => {
      this.send('reloadModel');
      this.alerts.success('people.unsubscribe', `${selectedPeopleCountFormatted} will be unsubscribed soon`);
    }, ignorePromiseFailure);

    return promise;
  }

  @action
  showColumnsModalAction() {
    this.set('showColumnsModal', true);
  }

  @action
  hideColumnsModal() {
    this.set('showColumnsModal', false);
  }

  @action
  setColumns(columns: string[]) {
    let promise = this.settings.saveEnvironmentSetting.perform('customerColumns', columns);

    promise.then(() => this.set('showColumnsModal', false), ignorePromiseFailure);

    return promise;
  }

  @action
  trackViewSearchFilter() {
    let filters = queryDecode(this.filters ?? queryEncode({ and: [] })) as Filters | SerializedLegacyFilter[];
    let operator = Array.isArray(filters) ? 'and' : Object.keys(filters)[0];
    let filterList = deserializeFilters(filters, false);
    let counts = filterList.reduce(
      (agg, filter) => {
        if (filter.type === 'segments_negatable') {
          agg.segments = agg.segments + 1;
        } else if (filter.type === 'attribute') {
          agg.attributes = agg.attributes + 1;
        } else if (filter.type === 'newsletter_recipients') {
          agg.newsletters = agg.newsletters + 1;
        } else if (filter.type === 'message' && (filter as SerializedLegacyMessageFilter).event?.type === 'event') {
          agg.events = agg.events + 1;
        } else if (filter.type === 'message') {
          agg.messages = agg.messages + 1;
        } else if (filter.type === ConditionType.RelationshipAttribute) {
          agg.relationship_attributes = agg.relationship_attributes + 1;
        }

        return agg;
      },
      { segments: 0, attributes: 0, newsletters: 0, messages: 0, events: 0, relationship_attributes: 0 },
    );

    let data: Record<string, any> = {
      operator,
      filteredByEmail: this.email !== '',
      emailIncludesWildcard: this.email.includes('*'),
      ...counts,
    };

    if (this.listIndex.source) {
      data.source = this.listIndex.source;
    }

    this.analytics.track('people', 'list_viewed_searched_filtered', data);
  }

  @action
  mergeSelectedPeople() {
    let [primaryCustomerId, secondaryCustomerId] = this.selectedIds;
    this.router.transitionTo('people.merge', { queryParams: { primaryCustomerId, secondaryCustomerId } });
  }

  @action
  selectAllPeopleOnPage(checked: boolean) {
    this.listIndex.selectAllPeopleOnPage(checked);
  }

  @action
  clearSelection() {
    this.listIndex.clearSelection();
  }
}

declare module '@ember/controller' {
  interface Registry {
    'people.list': PeopleListController;
  }
}
